import { EmbedSignDocument } from '@documenso/embed-react';
import { Box, CircularProgress } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { _postDocumentsAgreement, PostDocumentsAgreementResponse } from '@/api/signing/mutations';
import { _getDocumentById } from '@/api/signing/queries';
import { SigningStatus } from '@/api/signing/types';
import { USER_DASHBOARD_QUERY_KEYS, UserProfileRes } from '@/api/user-dashboard/queries';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { LoadingWrapper } from '@/pages/referral/shared/styles';
import CustomModal from '@/shared/components/custom-modal';

export interface SignDocumentModalProps {
  user: UserProfileRes;
}

export const SignDocumentModal = () => {
  const { handleClose, open, props } = useGlobalModal('SIGN_DOCUMENT_MODAL');
  const [agreement, setAgreement] = useState<PostDocumentsAgreementResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const queryClient = useQueryClient();

  const { mutate: createAgreement, isPending: isCreating } = useMutation({
    mutationFn: _postDocumentsAgreement,
    onSuccess: (res) => {
      setAgreement(res);
      setIsPolling(false);
    },
  });

  useEffect(() => {
    if (!open) return;
    setAgreement(null);
    setIsPolling(false);
    createAgreement();
  }, [open, createAgreement]);

  const { data: document } = useQuery({
    ..._getDocumentById(agreement?.documentId),
    enabled: !!agreement?.documentId && isPolling,
    refetchInterval: isPolling ? 1000 : false,
  });

  const handleDocumentCompleted = useCallback(() => {
    if (!agreement?.documentId) return;
    setIsPolling(true);
  }, [agreement?.documentId]);

  useEffect(() => {
    if (!document) return;

    if (document.signingStatus === SigningStatus.Signed) {
      queryClient.invalidateQueries({
        queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile],
      });
      setIsPolling(false);
      handleClose();
    }
  }, [document]);

  const token = agreement?.signingToken;
  const signerName = props?.user?.name || props?.user?.email;

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      sx={{ maxWidth: '1500px', width: '90%', maxHeight: '1400px', height: '85vh' }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          '& .gp-doc-sign-embed': { width: '100%', height: '100%', borderRadius: '12px' },
        }}
      >
        {token && signerName && (
          <EmbedSignDocument
            name={signerName}
            token={token}
            className="gp-doc-sign-embed"
            onDocumentCompleted={handleDocumentCompleted}
            lockName
          />
        )}
        {(isCreating || isPolling) && (
          <LoadingWrapper>
            <CircularProgress size={28} />
          </LoadingWrapper>
        )}
      </Box>
    </CustomModal>
  );
};
